import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { fetchDictTypesApi, fetchDictTypesApiPublic } from "@/api";

export const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  /**
   * 字典数据
   * - 由后端字典接口返回的各种枚举配置
   */
  const [dicts, setDicts] = useState({});

  /**
   * 当前登录用户信息
   * - 初始值从 sessionStorage 中恢复，避免刷新后丢失
   * - 登录/登出时由业务代码调用 setUser 进行更新
   */
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch (e) {
      console.error("解析本地用户信息失败:", e);
      return {};
    }
  });

  /**
   * 当前用户持有的权限 code 列表
   * - 从 user.permissions 中派生，保持只读
   */
  const permissions = user?.permissions || [];

  /**
   * 统一的权限判断函数
   * - 页面/组件通过 StoreContext 使用
   * - 后续如果权限来源发生变化，只需要修改这里一处
   */
  const hasPermission = useCallback(
    (permissionCodes, requireAll = false) => {
      if (!permissionCodes) {
        return true; // 没有权限要求，默认允许
      }

      const codes = Array.isArray(permissionCodes)
        ? permissionCodes
        : [permissionCodes];

      if (codes.length === 0) {
        return true;
      }

      if (requireAll) {
        // 需要同时拥有所有权限
        return codes.every((code) => permissions.includes(code));
      }

      // 只要拥有其中一个权限即可
      return codes.some((code) => permissions.includes(code));
    },
    [permissions]
  );

  const fetchDictTypesService = useCallback(async () => {
    try {
      // 检查是否已登录
      const token = sessionStorage.getItem("token");
      // 如果已登录，使用需要认证的接口（可获取所有数据）
      // 如果未登录，使用公共接口（只返回启用的数据）
      const response = token
        ? await fetchDictTypesApi({ enabled: true })
        : await fetchDictTypesApiPublic();
      const _dicts = {};
      response.data.forEach((type) => {
        _dicts[type.value] = type.items;
      });
      setDicts(_dicts);
    } catch (error) {
      console.error("获取字典类型列表失败:", error);
      setDicts({});
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await fetchDictTypesService();
    };
    loadData();
  }, []);

  return (
    <StoreContext.Provider
      value={{
        // 字典数据
        dicts,
        // 刷新字典数据（供外部调用）
        refreshDicts: fetchDictTypesService,
        // 用户与权限相关（统一入口）
        user,
        permissions,
        setUser,
        hasPermission,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}
